import { useCallback } from "react";
import type { Category, PlanEvent } from "../../../shared/types";

interface UseScheduleImportExportProps {
  readonly events: PlanEvent[];
  readonly onAddEventFull: (data: Omit<PlanEvent, "id" | "createdAt">) => void;
}

export function useScheduleImportExport({
  events,
  onAddEventFull,
}: UseScheduleImportExportProps) {
  const handleExport = useCallback(() => {
    const recurringEvents = events.filter((e) => e.recurrence && !e.detachedFromId);
    const blob = new Blob([JSON.stringify(recurringEvents, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "palnia-template.json";
    a.click();
    URL.revokeObjectURL(url);
  }, [events]);

  const handleImport = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const data = JSON.parse(reader.result as string) as unknown[];
          if (!Array.isArray(data)) return;
          for (const item of data) {
            const ev = item as Record<string, unknown>;
            if (
              typeof ev.title === "string" &&
              typeof ev.date === "string" &&
              typeof ev.startTime === "string" &&
              typeof ev.endTime === "string" &&
              ev.recurrence
            ) {
              onAddEventFull({
                title: ev.title,
                category: (ev.category as Category) ?? "personal",
                date: ev.date,
                startTime: ev.startTime,
                endTime: ev.endTime,
                description: (ev.description as string) ?? "",
                notes: (ev.notes as string) ?? "",
                tags: Array.isArray(ev.tags) ? (ev.tags as string[]) : [],
                recurrence: ev.recurrence as PlanEvent["recurrence"],
                reminderMinutes:
                  typeof ev.reminderMinutes === "number" ? ev.reminderMinutes : null,
                reminderTime: typeof ev.reminderTime === "string" ? ev.reminderTime : null,
                reminderType: (ev.reminderType as "BEFORE" | "AT_TIME" | null) ?? null,
              });
            }
          }
        } catch {
          // Invalid JSON
        }
      };
      reader.readAsText(file);
    },
    [onAddEventFull],
  );

  return { handleExport, handleImport };
}
